import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref } from 'vue';
import ChartHeader from '@/modules/Statistics/components/ChartHeader.vue';
import { DashboardReportFields } from '@/modules/Statistics/interfaces/DashboardReport';
import { SortFunctions } from '@/modules/Statistics/interfaces/SortFunctions';
import { TableSorter } from '@/modules/Statistics/interfaces/TableSorter';
import { TableColumn } from '@/modules/Statistics/interfaces/StatisticsTableColumn';
import { formatValueForDashboard } from '@/helpers/NumberFormatter';

interface Props {
    data: DashboardReportFields;
    isEdit: boolean;
    isPrinting?: boolean;
    tablePagination: {
        paginationSize: number;
        activePrintingReport: string;
    };
}


export default /*#__PURE__*/_defineComponent({
  __name: 'TableReport',
  props: {
    data: null,
    isEdit: { type: Boolean },
    isPrinting: { type: Boolean, default: false },
    tablePagination: null
  },
  setup(__props: any) {

const props = __props as { data: DashboardReportFields, isEdit: boolean, isPrinting: boolean, tablePagination: {
        paginationSize: number;
        activePrintingReport: string;
    } };



const { proxy } = getCurrentInstance()!;

const cellPaddingClass = ref<string>('cell-padding--medium');
const sortedTableData = ref<null | object>(null);

const reportSize = computed(() => {
    switch (props.data.size) {
        case 'S':
            return {
                width: '48%',
            };
        default:
            return {
                width: '100%',
            };
    }
});

const pageSize = computed(() => {
    if (props.isPrinting && props.data.shouldExpandTable) {
        return false;
    }

    if (props.isPrinting && props.tablePagination.activePrintingReport === props.data.id) {
        return false;
    }

    return { pageSize: props.tablePagination.paginationSize };
});

const responsiveFontSize = computed(() => {
    switch (props.data.size) {
        case 'S':
            cellPaddingClass.value = 'cell-padding--small';
            return {
                'font-size': '0.6rem',
                color: '#000',
            };
        case 'M':
            cellPaddingClass.value = 'cell-padding--medium';
            return {
                'font-size': '0.75rem',
            };
        case 'L':
            cellPaddingClass.value = 'cell-padding--large';
            return {
                'font-size': '0.9rem',
            };
    }
});

const columns = computed(() => {
    if (props.data.statisticReport.data.columns) {
        return props.data.statisticReport.data.columns.map((column: Record<string, any>) => {
            const baseObject = {
                class: cellPaddingClass.value,
                title: proxy?.$t(column.name),
                dataIndex: column.name.toLowerCase(),
                key: column.name.toLowerCase(),
                defaultSortOrder: 'descend',
                sorter: true,
                type: column.type,
            } as {
                class: string;
                title: string;
                dataIndex: any;
                key: any;
                defaultSortOrder: string;
                sorter: boolean;
                type: any;
                width?: any;
            };

            if (column.width) {
                baseObject.width = column.width;
            }
            return baseObject;
        });
    }
    return [];
});

const tableData = computed(() => {
    if (props.data.statisticReport.data.values) {
        return props.data.statisticReport.data.values.map((row: Array<any>, rowIndex: number) => {
            const newRow: any = {};

            columns.value.forEach((column: TableColumn, columnIndex: number) => {
                newRow.key = rowIndex;

                const isPriceValue = column.key.includes('€');
                let columnValue = formatValueForDashboard(row[columnIndex], isPriceValue);

                if (typeof columnValue === 'string') {
                    newRow[column.key] = proxy?.$t(columnValue);
                } else {
                    newRow[column.key] = columnValue;
                }
            });
            return newRow;
        });
    }
    return [];
});

function changeChartSize(size: 'S' | 'M' | 'L') {
    props.data.size = size;
}

function onChange(_: any, __: any, sorter: TableSorter, dataSource: { currentDataSource: Array<Record<string, any>> }) {
    // Sort data based on data type
    let data = dataSource?.currentDataSource?.slice();
    if (!data || !sorter?.order) {
        return;
    }

    const sortFunctions: SortFunctions = {
        string: (a: string, b: string) => a.localeCompare(b) as 1 | -1,
        number: (a: number, b: number) =>
            parseFloat(String(a).replace(/, /g, '')) - parseFloat(String(b).replace(/, /g, '')),
        date: (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
    };

    const sortOrder = sorter.order === 'ascend' ? 1 : -1;

    data = data.sort((a: Record<string, any>, b: Record<string, any>) => {
        return sortFunctions[sorter.column.type](a[sorter.columnKey], b[sorter.columnKey]) * sortOrder;
    });

    sortedTableData.value = data;
}

return { __sfc: true,props, proxy, cellPaddingClass, sortedTableData, reportSize, pageSize, responsiveFontSize, columns, tableData, changeChartSize, onChange, ChartHeader }
}

})